import React, { useCallback, useMemo, useState } from 'react'
import { isEmpty } from 'lodash'
import { IconExternalLink } from '@tabler/icons-react'

import Select from '@/components/forms/Select'
import { Button } from '@/components/buttons/Button'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { FormField } from '@/components/forms/FormField'
import { TextInput } from '@/components/forms/TextInput'
import { CARTESIA_DASHBOARD_URL } from '@/reducers/agentProvisioning/agentProvisioning.constants'

// Step 1 — the Cartesia agent (the real-world starting point: it's already been
// created on Cartesia's dashboard). Pick the tenant it lives under, then choose
// the agent by name from the tenant's account OR paste its id — one combined
// field — and validate it against Cartesia (footer "Validate & continue"). The
// tenant list isn't org-scoped, so this can run before org selection.
export const CartesiaStep = ({
  tenants,
  tenantsLoading,
  tenantId,
  onSelectTenant,
  onCreateTenant,
  createTenantLoading,
  createTenantError,
  cartesiaAgents,
  cartesiaAgentsLoading,
  agentId,
  onChange,
}) => {
  const [creatingTenant, setCreatingTenant] = useState(false)
  const [name, setName] = useState('')
  const [tokenEnvVar, setTokenEnvVar] = useState('')

  const tenantOptions = tenants.map((tenant) => ({ value: tenant.id, label: tenant.name }))
  // The list is the tenant's onboardable agents (already-linked ones filtered
  // out). Menu lists agents by name; the selected value renders as the agent_id
  // (see formatAgentOption). Free text is allowed so an agent not in this cached
  // snapshot (e.g. just created on the dashboard) can still be entered — nothing
  // is validated until Continue. Memoized for stable react-select identities.
  const agentOptions = useMemo(
    () =>
      cartesiaAgents
        .filter((agent) => !agent.in_use)
        .map((agent) => ({ value: agent.id, label: agent.name || agent.id })),
    [cartesiaAgents]
  )

  // Menu shows the friendly name; the control (selected value) shows the
  // agent_id, so the field stays id-centric whether you pick or type.
  const formatAgentOption = useCallback(
    (option, { context }) => (context === 'menu' ? option.label : option.value),
    []
  )

  // Let the operator find an agent by typing/pasting either its id or its name.
  const filterAgentOption = useCallback(
    (option, input) =>
      !input ||
      option.value.toLowerCase().includes(input.toLowerCase()) ||
      option.label.toLowerCase().includes(input.toLowerCase()),
    []
  )

  // Free-text commit label (no "create" — it just uses the typed id, which gets
  // checked on Continue). Suppressed when the input already matches a listed id.
  const formatCreateLabel = useCallback((input) => `Use “${input}”`, [])
  const isValidNewOption = useCallback(
    (input, _selectValue, options) =>
      Boolean(input) && !options.some((option) => option.value === input),
    []
  )

  const handleCreateTenant = async () => {
    const created = await onCreateTenant({ name: name.trim(), tokenEnvVar: tokenEnvVar.trim() })
    if (created) {
      setCreatingTenant(false)
      setName('')
      setTokenEnvVar('')
    }
  }

  return (
    <div className="create-agent__step" data-testid="cartesia-step">
      <h2>Cartesia agent</h2>
      <ol className="create-agent__instructions">
        <li>
          Create the agent in the{' '}
          <a
            className="create-agent__external-link"
            href={CARTESIA_DASHBOARD_URL}
            target="_blank"
            rel="noreferrer"
          >
            Cartesia dashboard <IconExternalLink size={14} />
          </a>
          .
        </li>
        <li>Pick the tenant it belongs to, then choose or paste its agent id below.</li>
        <li>
          On continue we verify the id exists in Cartesia. If the agent has its own backend code,
          have its slug ready for the last step: usually its <code>ROUTER_NAME</code> environment
          variable.
        </li>
      </ol>

      {creatingTenant ? (
        <div className="field-stack">
          <FormField>
            <label htmlFor="tenant-name">Tenant name</label>
            <TextInput
              id="tenant-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </FormField>
          <FormField>
            <label htmlFor="tenant-token">Token env var name</label>
            <TextInput
              id="tenant-token"
              value={tokenEnvVar}
              placeholder="CARTESIA_TOKEN_ACME"
              onChange={(event) => setTokenEnvVar(event.target.value)}
            />
            <div className="form-caption">
              The name of the env var that stores the API token — not the token itself.
            </div>
          </FormField>
          {createTenantError && <ErrorMessage content={createTenantError} />}
          <div className="create-agent__inline-actions">
            <Button small onClick={() => setCreatingTenant(false)} disabled={createTenantLoading}>
              Cancel
            </Button>
            <Button
              brand
              small
              loading={createTenantLoading}
              disabled={isEmpty(name.trim()) || isEmpty(tokenEnvVar.trim())}
              onClick={handleCreateTenant}
            >
              Create tenant
            </Button>
          </div>
        </div>
      ) : (
        <div className="field-stack">
          <FormField>
            <label htmlFor="tenant-select">Tenant</label>
            <Select
              inputId="tenant-select"
              options={tenantOptions}
              value={tenantId}
              loading={tenantsLoading}
              isClearable={false}
              placeholder="Select a tenant…"
              onChange={(option) => onSelectTenant(option?.value ?? null)}
            />
          </FormField>
          <Button basic small onClick={() => setCreatingTenant(true)}>
            + Create new tenant
          </Button>

          {tenantId && (
            <FormField>
              <label htmlFor="cartesia-agent">Cartesia agent</label>
              {/* One field: pick a listed agent, or type/paste an id (an id not
                  in the cached list is still accepted). Nothing is validated here
                  — Continue checks the id against Cartesia. */}
              <Select
                allowFreeText
                inputId="cartesia-agent"
                options={agentOptions}
                value={agentId}
                isLoading={cartesiaAgentsLoading}
                formatOptionLabel={formatAgentOption}
                filterOption={filterAgentOption}
                formatCreateLabel={formatCreateLabel}
                isValidNewOption={isValidNewOption}
                placeholder="Select an agent by name or id…"
                onChange={(option) => onChange('agentId', option?.value ?? '')}
              />
            </FormField>
          )}
        </div>
      )}
    </div>
  )
}
